/**
 * Usage & cost — the real ledger, not a placeholder.
 *
 * Every Claude and Whisper call this deployment makes appends a row to
 * data/usage/<YYYY-MM>.jsonl with what it was FOR (scope), which model,
 * how many tokens and what that cost. GET /api/ai/usage?days=N returns
 * the window's rows plus a summary; this screen is that summary.
 *
 * Two honesty rules the numbers follow:
 *   - An UNPRICED row (a model the catalog doesn't price) counts toward
 *     "Calls" but adds nothing to the money, and renders as "—" rather
 *     than a guessed number.
 *   - The BALANCE may be negative. That is not an error state to hide:
 *     we never block a render for money, so the number goes red and
 *     Settings says "Add credit".
 */
import { AlertTriangle, DollarSign, Loader2, TrendingDown, Wallet, Zap } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { getAiUsage, type AiUsageReport } from "../api";
import { PageHeader } from "../components/PageHeader";
import { formatUsd } from "../lib/chatThreads";
import { useIsMobile } from "../lib/useIsMobile";

const DAY_OPTIONS = [7, 30, 90] as const;

const panelStyle: React.CSSProperties = {
  background: "rgba(16,18,27,.72)",
  border: "1px solid rgba(255,255,255,.06)",
  borderRadius: 16,
  padding: 18,
};

const labelStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 11,
  color: "var(--ql-fg-muted)",
  textTransform: "uppercase",
  letterSpacing: ".06em",
  fontWeight: 700,
  marginBottom: 10,
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: ".06em",
  textTransform: "uppercase",
  color: "var(--ql-fg-subtle)",
  padding: "0 10px 8px 0",
  whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: "var(--ql-fg-muted)",
  padding: "7px 10px 7px 0",
  borderTop: "1px solid rgba(255,255,255,.05)",
  whiteSpace: "nowrap",
};

const monoTd: React.CSSProperties = {
  ...tdStyle,
  fontFamily: "var(--ql-font-mono)",
  color: "var(--ql-fg)",
};

/** Money to 4 decimals for per-call rows — a cheap call is $0.0007 and
 *  rounding it to cents would print a wall of "$0.00". */
function fine(usd: number | null): string {
  return usd === null ? "—" : formatUsd(usd, 4);
}

/** Days of the current UTC month that have started, so a window of this
 *  length always reaches back past the 1st. Minimum 1 (the 1st itself). */
function monthToDateDays(now = new Date()): number {
  return Math.max(1, now.getUTCDate());
}

/** Spend inside the CURRENT calendar month, summed from a report whose
 *  window is known to cover month-to-date. Summing the SELECTED window
 *  instead was the bug this replaces: with "7 days" picked on the 20th,
 *  the tile labelled "calendar month" showed one week. */
function monthTotalUsd(report: AiUsageReport): number {
  const prefix = new Date().toISOString().slice(0, 7); // YYYY-MM, UTC
  return report.summary.byDay
    .filter((d) => d.day.startsWith(prefix))
    .reduce((sum, d) => sum + d.usd, 0);
}

function Table({
  head,
  children,
}: {
  head: string[];
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {head.map((h) => (
              <th key={h} style={thStyle}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Cost(): React.JSX.Element {
  const isMobile = useIsMobile();
  const [days, setDays] = useState<number>(30);
  const [report, setReport] = useState<AiUsageReport | null>(null);
  /** Month-to-date spend — independent of the selected window. null = the
   *  month fetch failed; the tile says so instead of showing the window. */
  const [monthUsd, setMonthUsd] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((d: number) => {
    let alive = true;
    setLoading(true);
    const windowed = getAiUsage(d);
    // A window at least as long as month-to-date already contains the whole
    // month — no second round trip for "30 days" or "90 days".
    const monthDays = monthToDateDays();
    const monthly = (d >= monthDays ? windowed : getAiUsage(monthDays))
      .then(monthTotalUsd)
      // A failed MONTH fetch must not blank the window the operator asked
      // for — only the one tile goes unknown.
      .catch(() => null);
    Promise.all([windowed, monthly])
      .then(([r, m]) => { if (alive) { setReport(r); setMonthUsd(m); setError(null); } })
      .catch((err: unknown) => {
        if (alive) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => load(days), [load, days]);

  const balance = report?.credits.balanceUsd ?? 0;
  const negative = report?.provider === "platform" && balance < 0;
  const tiles = report
    ? [
        {
          lbl: "Balance",
          num: report.provider === "own" ? "Own key" : formatUsd(balance),
          sub:
            report.provider === "own"
              ? "Anthropic bills you directly"
              : negative
                ? "Negative — add credit in Settings"
                : `${formatUsd(report.credits.spentUsd)} spent all-time`,
          Icon: Wallet,
          color: negative ? "var(--ql-danger)" : "var(--ql-yellow)",
        },
        {
          lbl: "This month",
          num: monthUsd === null ? "—" : formatUsd(monthUsd),
          sub: monthUsd === null ? "month-to-date unavailable" : "calendar month, UTC",
          Icon: DollarSign,
          color: "#4ADE80",
        },
        {
          lbl: `Last ${report.days} days`,
          num: formatUsd(report.summary.totalUsd),
          sub: "AI calls we have prices for",
          Icon: TrendingDown,
          color: "#60A5FA",
        },
        {
          lbl: "Calls",
          num: String(report.summary.count),
          sub: `${Object.keys(report.summary.byModel).length} models · across ${Object.keys(report.summary.byScope).length} kinds of work`,
          Icon: Zap,
          color: "#A78BFA",
        },
      ]
    : [];

  const byScope = report
    ? Object.entries(report.summary.byScope).sort((a, b) => b[1].usd - a[1].usd)
    : [];
  const byModel = report
    ? Object.entries(report.summary.byModel).sort((a, b) => b[1].usd - a[1].usd)
    : [];
  const byDay = report ? [...report.summary.byDay].reverse() : [];
  const rows = report ? report.rows.slice(0, 50) : [];
  const empty = !!report && report.summary.count === 0;

  return (
    <div>
      <PageHeader
        eyebrow="Usage & cost"
        title="What the AI spent."
        subtitle="Every AI call this deployment makes is metered to a ledger — what it was for, which model, and what it cost. Calls made with your own key are recorded too, but billed to that key."
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", gap: 4 }}>
          {DAY_OPTIONS.map((d) => {
            const active = d === days;
            return (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                style={{
                  padding: "7px 14px",
                  borderRadius: 10,
                  fontFamily: "inherit",
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: "pointer",
                  border: active ? "1px solid rgba(255,213,0,.30)" : "1px solid rgba(255,255,255,.07)",
                  background: active ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.02)",
                  color: active ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
                }}
              >
                {d} days
              </button>
            );
          })}
        </div>
        {report && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 11px",
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,.10)",
              background: "rgba(255,255,255,.03)",
              fontSize: 11.5,
              fontWeight: 700,
              color: "var(--ql-fg-muted)",
            }}
          >
            {report.provider === "own" ? "Your own Anthropic key" : "Platform credits"}
          </span>
        )}
        {loading && <Loader2 size={14} className="ql-spin" style={{ color: "var(--ql-fg-subtle)" }} />}
      </div>

      {error && (
        <div
          style={{
            ...panelStyle,
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
            marginBottom: 20,
            borderColor: "rgba(248,113,113,.30)",
            background: "rgba(248,113,113,.06)",
            color: "var(--ql-danger)",
            fontSize: 13,
          }}
        >
          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ minWidth: 0 }}>
            Couldn&apos;t load usage: {error}
            <div>
              <button
                type="button"
                onClick={() => load(days)}
                style={{
                  marginTop: 8,
                  padding: "6px 12px",
                  borderRadius: 9,
                  border: "1px solid rgba(248,113,113,.35)",
                  background: "rgba(248,113,113,.08)",
                  color: "var(--ql-danger)",
                  fontFamily: "inherit",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {report && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
              gap: 14,
              marginBottom: 24,
            }}
          >
            {tiles.map((t) => (
              <div key={t.lbl} style={panelStyle}>
                <div style={labelStyle}>
                  <t.Icon size={12} strokeWidth={2} color={t.color} />
                  {t.lbl}
                </div>
                <div
                  style={{
                    fontSize: 26,
                    fontWeight: 800,
                    fontFamily: "var(--ql-font-mono)",
                    letterSpacing: "-0.02em",
                    color: t.color,
                  }}
                >
                  {t.num}
                </div>
                <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginTop: 4 }}>
                  {t.sub}
                </div>
              </div>
            ))}
          </div>

          {empty ? (
            <div
              style={{
                ...panelStyle,
                padding: 28,
                color: "var(--ql-fg-muted)",
                fontSize: 13,
                lineHeight: 1.65,
              }}
            >
              Nothing metered in the last {report.days} days yet. Render a clip or chat with the
              design agent and every call lands here — by scope, by model, by day.
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: 14,
                  marginBottom: 14,
                }}
              >
                <div style={panelStyle}>
                  <div style={labelStyle}>By scope</div>
                  <Table head={["Scope", "Calls", "USD"]}>
                    {byScope.map(([scope, v]) => (
                      <tr key={scope}>
                        <td style={tdStyle}>{scope}</td>
                        <td style={tdStyle}>{v.count}</td>
                        <td style={monoTd}>{formatUsd(v.usd)}</td>
                      </tr>
                    ))}
                  </Table>
                </div>

                <div style={panelStyle}>
                  <div style={labelStyle}>By model</div>
                  <Table head={["Model", "Calls", "In", "Out", "USD"]}>
                    {byModel.map(([model, v]) => (
                      <tr key={model}>
                        <td style={{ ...tdStyle, fontFamily: "var(--ql-font-mono)" }}>{model}</td>
                        <td style={tdStyle}>{v.count}</td>
                        <td style={tdStyle}>{v.inputTokens.toLocaleString()}</td>
                        <td style={tdStyle}>{v.outputTokens.toLocaleString()}</td>
                        <td style={monoTd}>{formatUsd(v.usd)}</td>
                      </tr>
                    ))}
                  </Table>
                </div>
              </div>

              <div style={{ ...panelStyle, marginBottom: 14 }}>
                <div style={labelStyle}>By day</div>
                <Table head={["Day", "Calls", "USD"]}>
                  {byDay.map((d) => (
                    <tr key={d.day}>
                      <td style={tdStyle}>{d.day}</td>
                      <td style={tdStyle}>{d.count}</td>
                      <td style={monoTd}>{formatUsd(d.usd)}</td>
                    </tr>
                  ))}
                </Table>
              </div>

              <div style={panelStyle}>
                <div style={labelStyle}>
                  Last {rows.length} calls
                  {report.summary.count > rows.length
                    ? ` of ${report.summary.count}`
                    : ""}
                </div>
                <Table head={["Time", "Scope", "Clip", "Model", "In / Out", "USD"]}>
                  {rows.map((r, i) => (
                    <tr key={`${r.at}-${i}`}>
                      <td style={tdStyle}>{new Date(r.at).toLocaleString()}</td>
                      <td style={tdStyle}>{r.scope || "—"}</td>
                      <td
                        style={{
                          ...tdStyle,
                          maxWidth: 220,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={r.clip ?? ""}
                      >
                        {r.clip ?? "—"}
                      </td>
                      <td style={{ ...tdStyle, fontFamily: "var(--ql-font-mono)" }}>{r.model}</td>
                      <td style={tdStyle}>
                        {r.kind === "whisper" && r.minutes !== undefined
                          ? `${r.minutes.toFixed(2)} min`
                          : `${r.inputTokens.toLocaleString()} / ${r.outputTokens.toLocaleString()}`}
                      </td>
                      <td style={monoTd}>{fine(r.usd)}</td>
                    </tr>
                  ))}
                </Table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
